interface AuthingSSOConstructorParams {
  appId: string;
  redirect: Redirect;
  verify?: Verify;
}

interface Verify {
  url: string;
  data?: unknown;
  debounce?: number;
  beforeFn?: () => void;
  afterFn?: (v: any) => void;
  catchFn?: (v: any) => void;
}

interface LoginOption {
  redirect?: boolean;
}

interface Redirect {
  origin: string;
  pathname?: string;
}

class InvalidParamsError extends Error {
  protected data: any;

  constructor(message: string, data?: any) {
    super(message);
    this.data = data;
  }
}

class Fortress {
  private _appId: string;
  private _origin: string;
  private _pathname: string = '/passport/signin';

  constructor(redirect: Redirect, appId: string) {
    if (!appId) {
      throw new InvalidParamsError('URL构造器必须传入appId');
    }

    this._appId = appId;
    this._origin = redirect.origin;

    if (typeof redirect.pathname === 'string')
      this._pathname = redirect.pathname;
  }

  build() {
    const urls = new URL(this._origin);

    urls.pathname = this._pathname;
    urls.searchParams.set('appId', this._appId);
    urls.searchParams.set(
      'serviceUrl',
      window.encodeURIComponent(window.location.href),
    );

    return urls;
  }
}

export class SSO {
  private appId = process.env.NEXT_PUBLIC_CIAM_APPID;

  private redirect: Redirect = {
    origin: '',
  };
  private timer: NodeJS.Timeout | null = null;

  public fortress?: Fortress;
  private _verify?: Verify;

  private getSearch() {
    const historySearch = new URL(window?.location?.href).search;
    const search = !historySearch
      ? `?${window.location.href.split('?')?.[1]}`
      : historySearch;

    return new URLSearchParams(search);
  }

  private getCTicket() {
    return this.getSearch().get('cTicket');
  }

  init(options: AuthingSSOConstructorParams) {
    if (!options.appId) {
      throw new InvalidParamsError('please pass the appid parameter');
    }

    this.appId = options.appId;
    this.redirect = options.redirect ?? 'https://i.smartmore.com';

    if (options.verify) this._verify = options.verify;

    this.fortress = new Fortress(this.redirect, this.appId);
  }

  async loginFortress({ redirect }: LoginOption = { redirect: true }) {
    const cTicket = this.getCTicket();
    if (!cTicket) {
      if (redirect) {
        this.gotoFortress();
      }

      return;
    }

    if (this.timer) return;

    this._verify?.beforeFn?.();
    this.timer = setTimeout(() => {
      if (!this._verify) {
        throw new InvalidParamsError('verify is not configured');
      }

      const data = { cTicket };

      if (!['undefined', 'object'].includes(typeof this._verify.data)) {
        throw new InvalidParamsError("verify's request body must be object");
      }

      fetch(this._verify.url, {
        method: 'post',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(
          typeof this._verify.data === 'object'
            ? { ...this._verify.data, ...data }
            : data,
        ),
      })
        .then((res) => {
          const data = res.json();
          return res.status === 200 && res.ok ? data : Promise.reject(data);
        })
        .then(this._verify?.afterFn)
        .catch((err) => {
          if (Object.prototype.toString.call(err) === '[object Promise]') {
            return err.then(this._verify?.catchFn).catch(this._verify?.catchFn);
          }

          this._verify?.catchFn?.(err);
        })
        .finally(() => {
          this.timer = null;
        });
    }, this._verify?.debounce ?? 200);
  }

  gotoFortress() {
    if (!this.appId) {
      throw new InvalidParamsError('please pass the appid parameter');
    }

    if (!this.fortress) {
      throw new InvalidParamsError('not instantly fortress');
    }

    const url = this.fortress.build();
    window.location.href = url.href;
  }
}

const sso = new SSO();

export const initFortress = sso.init.bind(sso);
export const gotoFortress = sso.gotoFortress.bind(sso);
export const loginFortress = sso.loginFortress.bind(sso);

export default sso;
